import { useEffect, useState, useMemo } from "react";
import { useAuth0 } from '@auth0/auth0-react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import '../../styles/main.css'
import 'bootstrap-icons/font/bootstrap-icons.css';
import { GetConnectionStatus } from "../../utils/GetConnectionStatus";
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Image from 'react-bootstrap/Image';
import questionMark from '../../images/question_mark.png';
import Table from 'react-bootstrap/Table';
import { getUserPermissions, isAuthorized, getAllowedBrokerages } from "../../utils/Permissions";
import { LoadingScreen } from "../LoadingScreen";
import { SchwabConnectionForm } from "./SchwabConnectionForm";
import { TradovateConnectionForm } from "./TradovateConnectionForm";
import { DeleteConnectionModal } from "./DeleteConnectionModal";
import { connectToTradovate, disconnectFromTradovate } from "../../api/tradovate";
import { BROKERAGE_SCHWAB, BROKERAGE_TRADOVATE } from '../../utils/constants';
import { connectToSchwab, disconnectFromSchwab, getSchwabConnectUrl } from '../../api/schwab';
import Badge from 'react-bootstrap/Badge';


const DEFAULT_FORM_STATE = {
        accountName: '',
        tradovateUsername: "",
        tradovatePassword: "",
        tradovateAppId: "tradovate_trader(web)",
        tradovateKey: "",
        tradovateDeviceID: "",
        tradovateCHL: "",
        tradovateAccountType: 'demo',
        autoConnect: false,
        accountStatus: [],
        buid: '',
        isConnected: false,
    }

export const BrokerConnections = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { getAccessTokenSilently, user } = useAuth0();
    const [userId, setUserId] = useState(null);
    const [schwabConnecting, setSchwabConnecting] = useState(false);
    const [connectionsLoaded, setConnectionsLoaded] = useState(false);
    const [connectionToDelete, setConnectionToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const allowedBrokerages = getAllowedBrokerages();

    const [formData, setFormData] = useState({
        ...DEFAULT_FORM_STATE,
        broker: allowedBrokerages[0]
    });

	// TODO: Need to handle cases when the stored values don't exist
	const storageObj = useMemo(() => {
		try {
			return window.sessionStorage;
		} catch {
			return null;
		}
	}, []);

    useEffect(() => {
        if(user) {
            setUserId(user?.sub?.split('|')[1] ?? null);
        }
    }, [user])

    //Get necessary data on screen load
    useEffect(() => {
        const savedBrokerSelection = storageObj?.getItem('connectionBrokerage');

        const removeStoredData = () => {
            storageObj.removeItem('connectionBrokerage');
            storageObj.removeItem('accountName');
            storageObj.removeItem('connectionAutomaticallyConnect');
            storageObj.removeItem('authorizationCode');
            storageObj.removeItem('schwabClientId');
            storageObj.removeItem('schwabClientSecret');
            storageObj.removeItem('reconnectBuid');
        };

        if(savedBrokerSelection === BROKERAGE_SCHWAB) {
            const paramCode = searchParams.get('authorizationCode');

            if(paramCode) {
                storageObj.setItem('authorizationCode', paramCode);
            }

            const code = paramCode || storageObj.getItem('authorizationCode');
            if (code && userId) {
                const connectUser = async () => {
                    setSchwabConnecting(true);
                    try {
                        let token = '';
                        token = await getAccessTokenSilently();

                        const reconnectBuid = storageObj.getItem('reconnectBuid');
                        const resp = await connectToSchwab({
                        	authorizationCode: code,
                            broker: storageObj.getItem('connectionBrokerage'),
                            accountName: storageObj.getItem('accountName'),
                            autoConnect: storageObj.getItem('connectionAutomaticallyConnect'),
                            clientId: storageObj.getItem('schwabClientId'),
                            clientSecret: storageObj.getItem('schwabClientSecret'),
                            ...(reconnectBuid && { buid: reconnectBuid }),
                        }, userId, token);
                        removeStoredData();
                        if (resp?.data?.buid) {
                            navigate(`/trade/${resp.data.buid}`);
                        }
                    } catch (error) {
                        removeStoredData();
                        const message = error?.message;
                        throw new Error(`Connection to Schwab was unsuccessful${message ? `: ${message}` : ''}`)
                    }
                };

                connectUser();
            }
        }
    }, [searchParams, storageObj, userId]);

    //Get necessary data on screen load
    useEffect(() => {
        if (!userId) return;

        const getConnectionStatus = async () => {
            let connectionList = [];
            let token = '';
            token = await getAccessTokenSilently();
            await getUserPermissions(token, userId);
            const connectionsResp = await GetConnectionStatus(token, userId);
            const connections = connectionsResp?.data;
            if (connections === 'Network Error') {
                setFormData(previousInputs => ({ ...previousInputs, isConnected: true, isActiveConnection: false }));
            } else {
                for (const userId in connections) {
                    connectionList.push({
                        name: connections[userId].name,
                        isConnected: connections[userId].isConnected,
                        isActiveConnection: connections[userId].isActiveConnection,
                        broker: connections[userId].broker || 'tradovate',
                        id: userId,
                    });
                }
                setFormData(previousInputs => ({ ...previousInputs, accountStatus: connectionList }));
            }
            setConnectionsLoaded(true);

        };
        getConnectionStatus();
        return () => { };
    }, [getAccessTokenSilently, userId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if(name === 'broker' && value !== formData.broker) {
            setFormData({ broker: value, ...DEFAULT_FORM_STATE });
        } else {
            setFormData((currentState) => ({
                ...currentState,
                [name]: value,
            }));
        }
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData((currentState) => ({
            ...currentState,
            [name]: checked,
        }));
    };

    const formComplete = () => {
        if (!formData.accountName) {
            return false;
        }

        if (formData.accountStatus.some((account) => account.name === formData.accountName)) {
            return false;
        }

        if(formData.broker === BROKERAGE_TRADOVATE) {
            return Boolean(
                formData.tradovateUsername &&
                formData.tradovatePassword &&
                formData.tradovateKey &&
                formData.tradovateDeviceID &&
                formData.tradovateCHL &&
                formData.tradovateAccountType
            );
        }

        if(formData.broker === BROKERAGE_SCHWAB) {
            return Boolean(formData.schwabClientId && formData.schwabClientSecret);
        }

        return true;
    
    };

    const handleConnect = async (e) => {
        e.preventDefault();
        let token = '';
        token = await getAccessTokenSilently();

        if(formData.broker === BROKERAGE_SCHWAB) {

            if (!formData.accountName || !formData.schwabClientId || !formData.schwabClientSecret) {
                alert('Please fill in all Schwab fields.');
                return;
            }

            if (storageObj) {
                storageObj.setItem('connectionBrokerage', formData.broker);
                storageObj.setItem('accountName', formData.accountName);
                storageObj.setItem('connectionAutomaticallyConnect', formData.autoConnect);
                storageObj.setItem('schwabClientId', formData.schwabClientId);
                storageObj.setItem('schwabClientSecret', formData.schwabClientSecret);
            }

            try {
                const resp = await getSchwabConnectUrl({ clientId: formData.schwabClientId }, userId, token);
                if (resp?.data?.url) {
                    window.location.assign(resp.data.url);
                }
            } catch (error) {
                alert('Error initiating Schwab connection. Please try again.');
            }

        } else {
            const resp = await connectToTradovate(formData, userId, token);
            // TODO: confirm status exists
            if(resp && resp.status === 200) {
                setFormData(prev => ({ ...prev, isConnected: true, buid: resp.data.buid }))
                navigate(`/trade/${resp.data.buid}`);
            }
        }
    };

    const handleDisconnect = (connection) => {
        setConnectionToDelete(connection);
    };

    const confirmDisconnect = async () => {
        if (!connectionToDelete) {
            return;
        }

        setDeleting(true);
        try {
            const token = await getAccessTokenSilently();
            const { name, id, broker } = connectionToDelete;

            if (broker === BROKERAGE_SCHWAB) {
                await disconnectFromSchwab(name, id, userId, token, formData, setFormData);
            } else {
                await disconnectFromTradovate(name, id, userId, token, formData, setFormData);
            }
            setConnectionToDelete(null);
        } finally {
            setDeleting(false);
        }
    };

    const handleReconnectSchwab = async (connection) => {
        if (storageObj) {
            storageObj.setItem('connectionBrokerage', BROKERAGE_SCHWAB);
            storageObj.setItem('accountName', connection.name);
            storageObj.setItem('reconnectBuid', connection.id);
        }

        try {
            const token = await getAccessTokenSilently();
            const resp = await getSchwabConnectUrl({ buid: connection.id }, userId, token);
            if (resp?.data?.url) {
                window.location.assign(resp.data.url);
            }
        } catch (error) {
            alert('Error initiating Schwab reconnection. Please try again.');
        }
    };

    const infoHover = (label, message) => (
        <span>
            {label} &nbsp;
            <OverlayTrigger
                key={label}
                placement="right"
                overlay={<Tooltip id={label}>{message}</Tooltip>}
            >
                <Image className="hover-question" src={questionMark} roundedCircle />
            </OverlayTrigger>
        </span>
    );

    if (!isAuthorized('connectionScreen')) {
        return null;
    }

    if (schwabConnecting) {
        return <LoadingScreen message="Connecting to Schwab..." />;
    }

    if (!connectionsLoaded) {
        return <LoadingScreen />;
    }

    return (
        <div>
            <DeleteConnectionModal
                show={!!connectionToDelete}
                connectionName={connectionToDelete?.name}
                deleting={deleting}
                onConfirm={confirmDisconnect}
                onCancel={() => { if (!deleting) setConnectionToDelete(null); }}
            />
            {formData.accountStatus.length > 0 && (
                <Container>
                    <Row>
                        <Col></Col>
                        <Col xs={10} className="bg-light mt-3 ml-3 mb-4 mr-3 pt-3 pb-3 justify-content-left">
                            <h3 className="border-bottom mb-2 pb-2">Connections</h3>
                            <Row>
                                <Col>
                                    <Table striped bordered hover>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '30%' }}>Name</th>
                                                <th style={{ width: '20%' }}>Brokerage</th>
                                                <th style={{ width: '25%' }}>Status</th>
                                                <th style={{ width: '25%' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.accountStatus.map((connection) => (
                                                <tr key={connection.id}>
                                                    <td>
                                                        <Link to={`/trade/${connection.id}`}>
                                                            {connection.name}
                                                        </Link>
                                                    </td>
                                                    <td style={{ textTransform: 'capitalize' }}>{connection.broker}</td>
                                                    <td>
                                                        {connection.isActiveConnection ? (
                                                            <Badge bg="success">Connected</Badge>
                                                        ) : connection.broker === BROKERAGE_SCHWAB ? (
                                                            <Badge bg="danger">Disconnected</Badge>
                                                        ) : (
                                                            <Badge bg="" style={{ border: '2px solid #6c757d', color: '#6c757d' }}>Outside Trading Hours</Badge>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            {connection.broker === BROKERAGE_SCHWAB && !connection.isActiveConnection && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-dark"
                                                                    onClick={() => handleReconnectSchwab(connection)}>
                                                                    <i className="bi bi-arrow-repeat me-1"></i>
                                                                    Reconnect
                                                                </Button>
                                                            )}
                                                            <Button
                                                                size="sm"
                                                                variant="outline-dark"
                                                                title="Delete connection"
                                                                onClick={() => handleDisconnect(connection)}>
                                                                <i className="bi bi-trash"></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                        </Col>
                        <Col></Col>
                    </Row>
                </Container>
            )}

            <Container>
                <Row>
                    <Col></Col>
                    <Col xs={10} className="bg-light mt-3 ml-3 mb-4 mr-3 pt-3 pb-3 justify-content-left">
                        <h3 className="border-bottom mb-2 pb-2">Create a Brokerage Connection</h3>
                        <Form className="main-form mb-4 pb-4 pt-2 bg-light">
                            <Form.Group className="mb-3" controlId="brokerage">
                                <Form.Label><h5>Brokerage</h5></Form.Label>
                                <Form.Select value={formData.broker} name='broker' onChange={handleChange} style={{textTransform: 'capitalize'}}>
                                    {allowedBrokerages.map((brokerage) => (
                                        <option key={brokerage} value={brokerage}>
                                            {brokerage}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="account-name">
                                <Form.Label><h5>Account Nickname</h5></Form.Label>
                                <Form.Control
                                    value={formData.accountName}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder={`${allowedBrokerages.find((brokerage) => brokerage === formData.broker)} Account Nickname`}
                                    name="accountName"
                                    required
                                    autoComplete='off'
                                />
                            </Form.Group>

                            {formData.broker === BROKERAGE_SCHWAB ? (
                                <SchwabConnectionForm values={formData} onChange={handleChange} />
                            ) : (
                                <TradovateConnectionForm
                                    values={formData}
                                    onChange={handleChange}
                                    allowLiveAccounts={isAuthorized('tradeLiveAccount')}
                                />
                            )}

                            {formData.broker !== BROKERAGE_SCHWAB && (
                                <Form.Group className="mb-3" controlId="autoConnect">
                                    <Form.Label><h5>Connect Automatically</h5></Form.Label>
                                    <Form.Check
                                        type="switch"
                                        label={infoHover(
                                            'Connect Automatically',
                                            'When toggled on, the app will automatically restore this connection on future reloads.'
                                        )}
                                        name="autoConnect"
                                        checked={formData.autoConnect}
                                        onChange={handleCheckboxChange}
                                        value={formData.autoConnect}
                                    />
                                </Form.Group>
                            )}
                            <div className="d-flex justify-content-center">
                                <Button
                                    style={{ textTransform: 'capitalize' }}
                                    variant="primary"
                                    type="submit"
                                    onClick={handleConnect}
                                    disabled={!formComplete()}
                                    >
                                    Connect {allowedBrokerages.find((brokerage) => brokerage === formData.broker)}
                                </Button>
                            </div>
                            <div>
                                <Link to="/trade" className="d-flex justify-content-left mt-5">
                                    Continue without connecting
                                </Link>
                            </div>
                        </Form>
                    </Col>
                    <Col></Col>
                </Row>
            </Container>
        </div>
    );
};
